// FILE: src/pages/profile/ProfileSecurityPage.jsx
// My profile › Security (MTO 1.1–1.3, glossary "Strong Password" and
// "Challenge Questions"): change password, change the five security
// questions, and the optional authenticator app. Every change asks for the
// current password; wrong passwords count toward the three-attempt lock.
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { useDispatch, useSelector } from "react-redux";
import fetchWithAuth from "../../utils/fetchWithAuth";
import { apiFetch } from "../../services/api";
import { fetchProfile } from "../../features/profileSlice";
import ProfileShell from "../../components/profile/ProfileShell";
import { LoadingState, Section, StatusChip } from "../../components/ui";
import PasswordRules from "../../components/PasswordRules";
import ShowQRCode from "../../components/ShowQRCode";
import { passwordFailures } from "../../utils/passwordPolicy";
import { errorBody, errorMessage } from "../../i18n/errors";

const REQUIRED = 5;

function formatDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" });
}

/** Plain-language failure + "N attempts left" when the server counted it. */
function FailureAlert({ error }) {
  if (!error) return null;
  const left = errorBody(error)?.attemptsRemaining;
  return (
    <Alert severity="error" sx={{ mb: 2 }} role="alert">
      {errorMessage(error)}
      {left != null ? ` ${left} ${left === 1 ? "attempt" : "attempts"} left before your account is locked.` : ""}
    </Alert>
  );
}

function post(path, body, method = "POST") {
  return fetchWithAuth(path, { method, body: JSON.stringify(body) });
}

function PasswordSection({ profile }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const weak = passwordFailures(next).length > 0;
  const changed = formatDate(profile?.passwordChangedAt);

  async function submit(e) {
    e.preventDefault();
    setTouched(true);
    setError(null);
    setDone(false);
    if (!current || weak || next !== confirm) return;
    setBusy(true);
    try {
      await post("/api/profile/password", { currentPassword: current, newPassword: next });
      setDone(true);
      setCurrent("");
      setNext("");
      setConfirm("");
      setTouched(false);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section
      id="password"
      title="Password"
      description={`${changed ? `Last changed ${changed}. ` : ""}For your security, passwords must be changed at least every 180 days.`}
    >
      {done && (
        <Alert severity="success" sx={{ mb: 2 }} role="status">
          Your password has been changed. We've emailed you a confirmation.
        </Alert>
      )}
      <FailureAlert error={error} />
      <Box component="form" onSubmit={submit} noValidate sx={{ maxWidth: 520 }}>
        <Stack spacing={2.5}>
          <TextField
            label="Current password"
            type="password"
            autoComplete="current-password"
            required
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            error={touched && !current}
            helperText={touched && !current ? "Enter your current password." : " "}
          />
          <TextField
            label="New password"
            type="password"
            autoComplete="new-password"
            required
            value={next}
            onChange={(e) => setNext(e.target.value)}
            error={touched && weak}
            helperText={touched && weak ? errorMessage("WEAK_PASSWORD") : " "}
            inputProps={{ "aria-describedby": "password-rules" }}
          />
          <PasswordRules password={next} />
          <TextField
            label="Confirm new password"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            error={touched && next !== confirm}
            helperText={touched && next !== confirm ? "The two passwords don't match." : " "}
          />
          <Box>
            <Button type="submit" variant="contained" disabled={busy}>
              {busy ? <CircularProgress size={22} color="inherit" aria-label="Saving" /> : "Change password"}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Section>
  );
}

function QuestionsSection({ profile, onChanged }) {
  const [open, setOpen] = useState(false);
  const [bank, setBank] = useState([]);
  const [answers, setAnswers] = useState(() => Array.from({ length: REQUIRED }, () => ({ questionId: "", answer: "" })));
  const [password, setPassword] = useState("");
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const count = profile?.challengeQuestionCount;

  useEffect(() => {
    if (!open || bank.length) return;
    apiFetch("/api/auth/challenge-questions")
      .then((r) => (r.ok ? r.json() : { questions: [] }))
      .then((d) => setBank(d.questions || []))
      .catch(() => setBank([]));
  }, [open, bank.length]);

  const chosen = useMemo(() => new Set(answers.map((a) => a.questionId).filter(Boolean)), [answers]);
  const setAnswer = (i, patch) => setAnswers((prev) => prev.map((a, j) => (j === i ? { ...a, ...patch } : a)));
  const invalid = answers.some((a) => !a.questionId || a.answer.trim().length < 2) || !password;

  async function submit(e) {
    e.preventDefault();
    setTouched(true);
    setError(null);
    if (invalid) return;
    setBusy(true);
    try {
      await post(
        "/api/security-questions",
        { password, questions: answers.map((a) => ({ questionId: a.questionId, answer: a.answer.trim() })) },
        "PATCH"
      );
      setDone(true);
      setOpen(false);
      setPassword("");
      setAnswers(Array.from({ length: REQUIRED }, () => ({ questionId: "", answer: "" })));
      setTouched(false);
      onChanged();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section
      id="questions"
      title="Security questions"
      description="During lessons we sometimes ask one of these to confirm it's really you (MTO identity check)."
      action={
        !open ? (
          <Button variant="outlined" onClick={() => setOpen(true)} sx={{ flexShrink: 0 }}>
            Change questions
          </Button>
        ) : null
      }
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: open ? 2 : 0 }}>
        {count >= REQUIRED ? (
          <StatusChip label={`${count} questions set`} tone="success" icon={<CheckCircleOutlineIcon />} />
        ) : (
          <StatusChip label="Not set up yet" tone="warning" icon={<RadioButtonUncheckedIcon />} />
        )}
      </Stack>
      {done && (
        <Alert severity="success" sx={{ mt: 2 }} role="status">
          Your new security questions are saved. Use these answers from now on.
        </Alert>
      )}
      <Collapse in={open} unmountOnExit>
        <FailureAlert error={error} />
        <Box component="form" onSubmit={submit} noValidate>
          <Typography sx={{ color: "text.secondary", mb: 2 }}>
            Choose five different questions and give answers you'll remember. Capitals and extra spaces don't matter.
          </Typography>
          {answers.map((a, i) => (
            <Stack key={i} direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 2 }}>
              <FormControl fullWidth required error={touched && !a.questionId} sx={{ flex: 3 }}>
                <InputLabel id={`sq${i}-label`}>{`Question ${i + 1}`}</InputLabel>
                <Select
                  labelId={`sq${i}-label`}
                  label={`Question ${i + 1}`}
                  value={a.questionId}
                  onChange={(e) => setAnswer(i, { questionId: e.target.value })}
                >
                  {bank.map((q) => (
                    <MenuItem key={q.id} value={q.id} disabled={chosen.has(q.id) && q.id !== a.questionId}>
                      {q.text}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label={`Answer ${i + 1}`}
                required
                autoComplete="off"
                value={a.answer}
                onChange={(e) => setAnswer(i, { answer: e.target.value })}
                error={touched && a.answer.trim().length < 2}
                helperText={touched && a.answer.trim().length < 2 ? "At least 2 characters." : " "}
                sx={{ flex: 2 }}
              />
            </Stack>
          ))}
          <TextField
            label="Current password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={touched && !password}
            helperText={touched && !password ? "Enter your password to confirm the change." : "We ask for it to confirm it's you."}
            sx={{ maxWidth: 420, width: "100%" }}
          />
          <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
            <Button onClick={() => setOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={busy}>
              {busy ? <CircularProgress size={22} color="inherit" aria-label="Saving" /> : "Save new questions"}
            </Button>
          </Stack>
        </Box>
      </Collapse>
    </Section>
  );
}

function AuthenticatorSection({ profile, onChanged }) {
  const enabled = !!profile?.twoFactorEnabled;
  const [step, setStep] = useState(null); // null | "password" | "scan" | "disable"
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [setup, setSetup] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const reset = () => {
    setStep(null);
    setPassword("");
    setCode("");
    setSetup(null);
    setError(null);
  };

  async function run(fn) {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  const startSetup = (e) => {
    e.preventDefault();
    run(async () => {
      const data = await post("/api/2fa/setup", { password });
      setSetup(data);
      setStep("scan");
      setPassword("");
    });
  };
  const confirmSetup = (e) => {
    e.preventDefault();
    run(async () => {
      await post("/api/2fa/verify", { token: code.trim() });
      reset();
      setNotice("The authenticator app is on. You'll enter its code when you sign in, as well as the emailed code.");
      onChanged();
    });
  };
  const disable = (e) => {
    e.preventDefault();
    run(async () => {
      await post("/api/2fa/disable", { password, token: code.trim() });
      reset();
      setNotice("The authenticator app is off. Sign-in still needs the code we email you.");
      onChanged();
    });
  };

  return (
    <Section
      id="authenticator"
      title="Authenticator app (optional)"
      description="An extra lock on top of the emailed code, using an app such as Google Authenticator or Microsoft Authenticator."
    >
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        {enabled ? (
          <StatusChip label="On" tone="success" icon={<CheckCircleOutlineIcon />} />
        ) : (
          <StatusChip label="Off" tone="default" icon={<RadioButtonUncheckedIcon />} />
        )}
      </Stack>
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} role="status" onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      <FailureAlert error={error} />

      {step === null &&
        (enabled ? (
          <Button variant="outlined" onClick={() => setStep("disable")}>
            Turn off the authenticator app
          </Button>
        ) : (
          <Button variant="outlined" onClick={() => setStep("password")}>
            Set up an authenticator app
          </Button>
        ))}

      {step === "password" && (
        <Box component="form" onSubmit={startSetup} noValidate sx={{ maxWidth: 420 }}>
          <Stack spacing={2}>
            <TextField
              label="Current password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              helperText="We ask for it to confirm it's you."
            />
            <Stack direction="row" spacing={1.5}>
              <Button onClick={reset}>Cancel</Button>
              <Button type="submit" variant="contained" disabled={busy || !password}>
                Continue
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}

      {step === "scan" && setup && (
        <Box component="form" onSubmit={confirmSetup} noValidate>
          <Typography sx={{ mb: 1.5 }}>
            Scan this code with your authenticator app, then type the 6-digit code it shows.
          </Typography>
          <ShowQRCode otpauthURL={setup.otpauthUrl} />
          <Typography sx={{ my: 1.5 }}>
            Can't scan? Enter this key in the app:{" "}
            <Box component="code" sx={{ overflowWrap: "anywhere" }}>
              {setup.secret}
            </Box>
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ alignItems: { sm: "flex-start" } }}>
            <TextField
              label="Code from the app"
              autoComplete="one-time-code"
              inputProps={{ inputMode: "numeric", maxLength: 6, pattern: "[0-9]*" }}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            />
            <Button onClick={reset}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={busy || code.length !== 6}>
              Turn on
            </Button>
          </Stack>
        </Box>
      )}

      {step === "disable" && (
        <Box component="form" onSubmit={disable} noValidate sx={{ maxWidth: 420 }}>
          <Stack spacing={2}>
            <TextField
              label="Current password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <TextField
              label="Code from the app"
              autoComplete="one-time-code"
              required
              inputProps={{ inputMode: "numeric", maxLength: 6, pattern: "[0-9]*" }}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            />
            <Stack direction="row" spacing={1.5}>
              <Button onClick={reset}>Cancel</Button>
              <Button type="submit" variant="contained" disabled={busy || !password || code.length !== 6}>
                Turn off
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}
    </Section>
  );
}

export default function ProfileSecurityPage() {
  const dispatch = useDispatch();
  const profile = useSelector((s) => s.profile.profile);
  const refresh = () => dispatch(fetchProfile());
  if (!profile) return <LoadingState variant="page" label="Loading your security settings…" />;

  return (
    <ProfileShell title="Security" description="Your password, security questions and sign-in protection.">
      <Stack spacing={3}>
        <Section id="sign-in-codes" title="Sign-in codes">
          <Stack direction="row" spacing={1.5} sx={{ alignItems: "flex-start" }}>
            <MarkEmailReadOutlinedIcon aria-hidden sx={{ color: "primary.main", mt: "2px" }} />
            <Typography>
              Every time you sign in, we email a one-time code to <strong>{profile.email}</strong>. This is always on:
              Ontario's rules for online driver education require two-step sign-in.
            </Typography>
          </Stack>
        </Section>
        <PasswordSection profile={profile} />
        <QuestionsSection profile={profile} onChanged={refresh} />
        <AuthenticatorSection profile={profile} onChanged={refresh} />
      </Stack>
    </ProfileShell>
  );
}
